<template>
  <InfuseTable
    :click-permission-id="chargingStationPermissions.GET"
    :columns="columns"
    :rows="rows"
    :loading="loading"
    :editable="editable"
    :removable="removable"
    :search="search"
    :has-more-items="hasMoreItems"
    :edit-permission-id="chargingStationPermissions.UPDATE"
    :remove-permission-id="chargingStationPermissions.DELETE"
    @click-row="(row: ChargingStation) => emit('click-row', row)"
    @load="emit('load')"
    @remove="(row: ChargingStation) => emit('remove', row)"
    @edit="(row: ChargingStation) => emit('edit', row)"
    @search="(query: string) => emit('search', query)"
  >
    <template #actions>
      <InfuseButton
        v-permission="{ id: chargingStationPermissions.IMPORT_CSV, hide: false }"
        v-if="importEnabled"
        class="w-12"
        color="neutral"
        size="medium"
        @click="emit('import')"
      >
        <font-awesome-icon :icon="['far', 'fa-arrow-up-to-line']" />
      </InfuseButton>
      <InfuseButton
        data-testid="page-create-button"
        v-permission="{ id: chargingStationPermissions.CREATE, hide: false }"
        v-if="createEnabled"
        class="w-64"
        size="medium"
        @click="emit('create')"
      >
        {{ t('e_connector.chargingStations.table.create_button') }}
      </InfuseButton>
    </template>

    <template #alias="{ data }">
      {{ data.join(', ') }}
    </template>

    <template #networkProfile="{ data }">
      <div class="italic" :title="data">{{ data }}</div>
    </template>

    <template #type="{ data }">
      {{ data || '-' }}
    </template>

    <template #ocppVersion="{ data }">
      <InfuseTag variant="neutral">OCPP-{{ data }}</InfuseTag>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { InfuseButton, InfuseTable, InfuseTag } from '@infuse/ui/components';
import { vPermission } from '@infuse/iam/permission/directives';
import type { UiTableColumn } from '@infuse/ui/domain';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChargingStation } from '@infuse/e-connector/charging-station/domain';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { chargingStationPermissions } from '@infuse/api/e-connector/charging-station';

interface Emits {
  (e: 'click-row', row: ChargingStation): void;
  (e: 'create'): void;
  (e: 'load'): void;
  (e: 'remove', row: ChargingStation): void;
  (e: 'edit', row: ChargingStation): void;
  (e: 'search', query: string): void;
  (e: 'import'): void;
}

const emit = defineEmits<Emits>();

interface Props {
  createEnabled?: boolean;
  editable: boolean;
  loading?: boolean;
  removable?: boolean;
  rows: ChargingStation[];
  searchEnabled: boolean;
  searchQuery?: string;
  importEnabled?: boolean;
  hasMoreItems: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  createEnabled: false,
  editable: false,
  loading: false,
  removable: false,
  searchEnabled: true,
  searchQuery: '',
  importEnabled: false
});

const { t } = useI18n();

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('e_connector.chargingStations.table.search'),
        query: props.searchQuery
      }
);

const columns: UiTableColumn[] = [
  {
    name: 'name',
    label: () => t('e_connector.common.chargingStation.name'),
    field: 'name',
    bold: true
  },
  {
    name: 'alias',
    label: () => t('e_connector.common.chargingStation.alias'),
    field: 'alias'
  },
  {
    name: 'networkProfile',
    label: () => t('e_connector.common.chargingStation.networkProfile'),
    field: 'networkProfileName'
  },
  {
    name: 'basicAuthStatus',
    label: () => t('e_connector.common.chargingStation.basicAuthStatus'),
    field: 'basicAuthStatus'
  },
  {
    name: 'type',
    label: () => t('e_connector.common.chargingStation.type'),
    field: 'type'
  },
  {
    name: 'ocppVersion',
    label: () => t('e_connector.common.chargingStation.ocppVersion'),
    field: 'ocppVersion'
  }
];
</script>
